import area from '@turf/area'
import intersect from '@turf/intersect'
import type { Feature, Polygon } from 'geojson'

import { boundsIntersect } from './boundsOverlap'
import { TerritoryBoundary, TerritoryBounds } from './typings'

/**
 * Does this shape already belong to somebody? (WWM-17508)
 *
 * Territories are allowed to overlap -- nothing here forbids it, and the performance report
 * already explains that a record inside two of a team's territories is counted in both. The
 * problem is that overlap is invisible while drawing and expensive to discover later: an
 * assignment rule returns the FIRST matching rule by `Order`, so when two territories cover the
 * same house the lower-ordered rule silently wins and a rep wonders why their leads went
 * elsewhere. That is not hypothetical -- it is exactly the "Guru not hung local" report, where
 * one territory turned out to be 79% nested inside another and nobody could see it.
 *
 * So this is a warning, never a block. Overlapping on purpose is legitimate; doing it by
 * accident is the bug.
 *
 * Why the client can answer this at all: geometry is fetched per viewport, and anything that
 * genuinely overlaps a shape drawn on screen must itself touch the screen -- so its boundary is
 * already in the cache. Where it is not (the user drew before the fetch landed) that id comes
 * back in `pendingIds` rather than being quietly treated as "no overlap", because a warning that
 * says "clear" when it simply did not look is worse than no warning at all.
 */

/** Exact, by definition: the international mile is 1609.344 m. */
const SQUARE_METRES_PER_SQUARE_MILE = 2589988.110336

/**
 * Smallest share worth mentioning, as a fraction of either shape.
 *
 * Two territories drawn along the same street do not meet on a mathematical line: freehand
 * strokes wobble, and simplification at ~2m leaves slivers where the borders cross back and
 * forth. A 2km shared border wandering by 10m is ~0.02 km^2, which against a 6 mi^2 territory is
 * well under a tenth of a percent -- noise, and reporting it would train admins to dismiss the
 * warning.
 *
 * Measured against EITHER shape, not the larger: a small territory swallowed whole by a big new
 * one is 100% of itself and perhaps 3% of the drawing, and it is the 100% that matters.
 */
export const MIN_REPORTABLE_FRACTION = 0.01

/** A territory the drawn shape might collide with. `boundary` is null until its geometry loads. */
export type OverlapCandidate = {
  id: number
  name: string
  bounds: TerritoryBounds
  boundary: TerritoryBoundary | null
}

export type TerritoryOverlap = {
  territoryId: number
  territoryName: string
  /** How much of the DRAWN shape is already inside this territory, 0..1. */
  sharedFractionOfDrawn: number
  /** How much of THIS TERRITORY the drawn shape covers, 0..1. The "swallowed whole" case. */
  sharedFractionOfExisting: number
  sharedSquareMiles: number
}

export type OverlapScan = {
  /** Worst first, by how much of the drawn shape is already taken. */
  overlaps: TerritoryOverlap[]
  /**
   * Bbox-overlapping territories whose geometry has not loaded. The scan is INCOMPLETE until
   * these are fetched and it is run again.
   */
  pendingIds: number[]
  /**
   * Territories whose geometry could not be compared. Fetching will not help -- the geometry is
   * present and turf refused it -- so the honest move is to say some could not be checked.
   */
  failedIds: number[]
}

/** The envelope of a ring, for the cheap reject before any real geometry runs. */
export function boundsOf(boundary: TerritoryBoundary): TerritoryBounds {
  let minLatitude = Infinity
  let maxLatitude = -Infinity
  let minLongitude = Infinity
  let maxLongitude = -Infinity

  for (const ring of boundary.coordinates) {
    for (const [longitude, latitude] of ring) {
      if (latitude < minLatitude) minLatitude = latitude
      if (latitude > maxLatitude) maxLatitude = latitude
      if (longitude < minLongitude) minLongitude = longitude
      if (longitude > maxLongitude) maxLongitude = longitude
    }
  }

  return { minLatitude, maxLatitude, minLongitude, maxLongitude }
}

const asFeature = (boundary: TerritoryBoundary): Feature<Polygon> => ({
  type: 'Feature',
  properties: {},
  geometry: boundary,
})

/**
 * Square miles shared by two rings, or null when turf could not answer.
 *
 * Null and 0 are different and the caller must keep them apart: 0 means "they do not touch",
 * null means "we do not know".
 */
function sharedArea(a: TerritoryBoundary, b: TerritoryBoundary): number | null {
  try {
    const shared = intersect({
      type: 'FeatureCollection',
      features: [asFeature(a), asFeature(b)],
    })

    return shared ? area(shared) : 0
  } catch {
    // Degenerate or self-touching input can trip the clipping library. One bad neighbour must
    // not take down the whole warning, so it is reported as unchecked instead.
    return null
  }
}

export function findTerritoryOverlaps(
  drawn: TerritoryBoundary,
  candidates: OverlapCandidate[],
  options: {
    /** The territory being redrawn. It overlaps its own old shape; saying so is just noise. */
    excludeId?: number | null
  } = {},
): OverlapScan {
  const { excludeId = null } = options

  const drawnArea = area(drawn)
  const drawnBounds = boundsOf(drawn)

  // A shape with no area shares no area. Guarding here rather than dividing by zero below, where
  // every fraction would come out NaN and sort unpredictably.
  if (!(drawnArea > 0)) return { overlaps: [], pendingIds: [], failedIds: [] }

  const overlaps: TerritoryOverlap[] = []
  const pendingIds: number[] = []
  const failedIds: number[] = []

  for (const candidate of candidates) {
    if (candidate.id === excludeId) continue
    if (!boundsIntersect(candidate.bounds, drawnBounds)) continue

    if (!candidate.boundary) {
      pendingIds.push(candidate.id)
      continue
    }

    const sharedSquareMetres = sharedArea(drawn, candidate.boundary)
    if (sharedSquareMetres === null) {
      failedIds.push(candidate.id)
      continue
    }
    if (sharedSquareMetres <= 0) continue

    const existingArea = area(candidate.boundary)
    const sharedFractionOfDrawn = sharedSquareMetres / drawnArea
    // An existing territory with no measurable area cannot be meaningfully covered; 0 keeps it
    // out of the max() below rather than making it Infinity and always reportable.
    const sharedFractionOfExisting =
      existingArea > 0 ? sharedSquareMetres / existingArea : 0

    const worst = Math.max(sharedFractionOfDrawn, sharedFractionOfExisting)
    if (worst < MIN_REPORTABLE_FRACTION) continue

    overlaps.push({
      territoryId: candidate.id,
      territoryName: candidate.name,
      sharedFractionOfDrawn,
      sharedFractionOfExisting,
      sharedSquareMiles: sharedSquareMetres / SQUARE_METRES_PER_SQUARE_MILE,
    })
  }

  overlaps.sort((a, b) => b.sharedFractionOfDrawn - a.sharedFractionOfDrawn)

  return { overlaps, pendingIds, failedIds }
}
